// ============================================================================
// DETAIL-MODAL TAB ROLE VISIBILITY
// ============================================================================
//
// Client users (`client_user`) get a deliberately limited view of CRM detail
// modals — they see only the tabs explicitly marked `clientVisible: true` on
// the entity's tab config. Employees/admins (`wtc_admin` / `wtc_crm` /
// `wtc_employee`) see every tab the existing `visible(item)` predicate allows.
//
// This is a pure helper so both detail surfaces — the modal
// (`EntityDetailModalLayout`) and the full detail page
// (`useDetailPageActions`) — share one rule and cannot drift.
//
// SECURITY NOTE: this gates which tabs are RENDERED in the UI. The real data
// boundary is Postgres RLS (see USER_ROLES_AND_ACCESS) — this is layered on
// top, not a substitute. A client should still never be handed a route/modal
// that links to data RLS would deny.

import type { TabConfig } from "@/components/shared/UniversalDetailPage/types/tab-types";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types/shared";

/**
 * Returns whether a tab is permitted for the current user's role class.
 *
 * - Employees/admins (`isClient === false`): always allowed (role check is a
 *   no-op; the caller still applies the per-item `visible()` predicate).
 * - Clients (`isClient === true`): allowed ONLY when `tab.clientVisible === true`.
 */
export function isTabAllowedForRole<T extends BaseEntity>(
  tab: TabConfig<T>,
  isClient: boolean,
): boolean {
  if (!isClient) return true;
  return tab.clientVisible === true;
}
